import React, { FC } from 'react';
import FormScreen from '../../../organisms/FormScreen';
import Pagehead from '@oberoninternal/travelbase-ds/components/section/Pagehead';
import {
    ConnectorEnum,
    useEditPartnerConnectorSettingsMutation,
    usePartnerConnectionsQuery,
} from '../../../../generated/graphql';
import Loading from '../../../organisms/Loading';
import { NotFoundError } from '../../../../entities/NotFoundError';
import { FormattedMessage, useIntl } from 'react-intl';
import useIsBackofficeuser from '../../../../hooks/useIsBackofficeuser';
import brandConfig from '../../../../constants/brandConfig';
import Heading from '../../../atoms/Heading';
import gql from 'graphql-tag';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import SelectInput from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { PartnerParams } from '../../../../entities/PartnerParams';
import { useParams } from 'react-router-dom';
import { ConnectionSettingEnum, parseConnectionSettings } from '../../../../utils/parseConnectionSettings';
import { useConnectionValidator } from '../../../../hooks/useConnectionValidator';
import { toast } from 'react-toastify';
import Toast from '@oberoninternal/travelbase-ds/components/feedback/Toast';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box, Flex } from '@rebass/grid';

export const connectionQuery = gql`
    query PartnerConnections($partnerId: ID!) {
        partner(id: $partnerId) {
            id
            connectorConfig {
                __typename
                ... on MaxxtonCredentialsConfig {
                    clientId
                    clientSecret
                    distributionChannelCode
                    scope
                }
                ... on MewsCredentialsConfig {
                    connectionToken
                }
            }
        }
    }
`;

export const editPartnerConnectorSettingsMutation = gql`
    mutation EditPartnerConnectorSettings($input: EditPartnerConnectorSettingsInput!) {
        editPartnerConnectorSettings(input: $input) {
            partner {
                id
                connectorConfig {
                    __typename
                }
            }
        }
    }
`;

const PartnerConnection: FC<React.PropsWithChildren<unknown>> = () => {
    const { partnerId } = useParams<PartnerParams>();
    const { data, loading } = usePartnerConnectionsQuery({ variables: { partnerId } });
    const [mutate] = useEditPartnerConnectorSettingsMutation();
    const validator = useConnectionValidator();
    const intl = useIntl();
    const { formatMessage } = intl;
    const isBackofficeUser = useIsBackofficeuser(partnerId);

    if (loading) {
        return <Loading />;
    }

    if (!data || !data.partner) {
        throw new NotFoundError();
    }
    const {
        partner: { id, connectorConfig },
    } = data;

    const hasMewsConfig = connectorConfig?.__typename === 'MewsCredentialsConfig';

    const initialValues = {
        connector: hasMewsConfig ? ConnectorEnum.Mews : ConnectorEnum.Maxxton,
        partnerId: id ?? '',
        connectionToken: (hasMewsConfig ? connectorConfig.connectionToken : '') ?? '',
        scope: (!hasMewsConfig ? connectorConfig?.scope : '') ?? '',
        clientId: (!hasMewsConfig ? connectorConfig?.clientId : '') ?? '',
        clientSecret: (!hasMewsConfig ? connectorConfig?.clientSecret : '') ?? '',
        distributionChannelCode: (!hasMewsConfig ? connectorConfig?.distributionChannelCode : '') ?? '',
    };

    const parseSettings = async (values: typeof initialValues) => {
        let parsed = null;

        if (values.connector === ConnectorEnum.Mews) {
            parsed = parseConnectionSettings(ConnectionSettingEnum.MEWS_CREDENTIALS_TYPE, values);
        }

        if (values.connector === ConnectorEnum.Maxxton) {
            parsed = parseConnectionSettings(ConnectionSettingEnum.MAXXTON_CREDENTIALS_TYPE, values);
        }

        if (!parsed) {
            return null;
        }

        const result = await validator.validateConnectionSettings(values.connector, parsed.settings);

        if (!result) {
            return null;
        }
        return parsed;
    };

    const clearSettings = async () => {
        await mutate({
            variables: {
                input: {
                    partnerId: id,
                    connector: null,
                    settings: null,
                },
            },
            refetchQueries: [{ query: connectionQuery, variables: { partnerId } }],
            awaitRefetchQueries: true,
        });
        toast(
            <Toast variant="success" title={formatMessage({ defaultMessage: 'Opgeslagen' })}>
                <Body variant="small">
                    <FormattedMessage defaultMessage="Succesvol opgeslagen" />
                </Body>
            </Toast>,
            {
                autoClose: 3000,
            }
        );
    };

    return (
        <FormScreen
            skipToast
            skipReset
            handleSubmit={async values => {
                const parsed = await parseSettings(values);

                if (!parsed) {
                    return;
                }

                await mutate({
                    variables: {
                        input: {
                            connector: parsed.connector,
                            partnerId: values.partnerId,
                            settings: parsed.settings,
                        },
                    },
                });
                toast(
                    <Toast variant="success" title={formatMessage({ defaultMessage: 'Opgeslagen' })}>
                        <Body variant="small">
                            <FormattedMessage defaultMessage="Succesvol opgeslagen" />
                        </Body>
                    </Toast>,
                    {
                        autoClose: 3000,
                    }
                );
            }}
            initialValues={initialValues}
        >
            {({ values }) => (
                <>
                    <Pagehead title={formatMessage({ defaultMessage: 'Koppeling instellen' })}>
                        <FormattedMessage defaultMessage="Hier voeg je nieuwe koppelingen toe." />
                    </Pagehead>
                    {(!brandConfig.hasCheckoutOnly || isBackofficeUser) && (
                        <>
                            <Heading title={formatMessage({ defaultMessage: 'kies koppeling' })} />
                            <SelectInput
                                variant="small"
                                name="connector"
                                options={[
                                    { value: ConnectorEnum.Mews, label: 'MEWS' },
                                    { value: ConnectorEnum.Maxxton, label: 'MAXXTON' },
                                ]}
                            />
                            <br />
                            <Heading title={formatMessage({ defaultMessage: 'Configuratie' })} />
                            {values.connector === ConnectorEnum.Mews ? (
                                <>
                                    <FormattedMessage defaultMessage="Channel manager code" />
                                    <TextInputField
                                        autoFocus
                                        variant="small"
                                        name="connectionToken"
                                        id="connectionToken"
                                    />
                                    <FormattedMessage defaultMessage="Deze code is te vinden op de instelling pagina van de travelbase koppeling in MEWS" />
                                </>
                            ) : (
                                <>
                                    <FormattedMessage defaultMessage="Scope" />
                                    <TextInputField autoFocus variant="small" name="scope" id="scope" />
                                    <FormattedMessage defaultMessage="Client id" />
                                    <TextInputField autoFocus variant="small" name="clientId" id="clientId" />
                                    <FormattedMessage defaultMessage="Client secret" />
                                    <TextInputField autoFocus variant="small" name="clientSecret" id="clientSecret" />
                                    <FormattedMessage defaultMessage="Distribution channelcode" />
                                    <TextInputField
                                        autoFocus
                                        variant="small"
                                        name="distributionChannelCode"
                                        id="distributionChannelCode"
                                    />
                                </>
                            )}
                            <br />
                            <br />
                            <Flex>
                                <Box mr={4}>
                                    <Button onClick={() => parseSettings(values)}>
                                        <FormattedMessage defaultMessage="test configuratie" />
                                    </Button>
                                </Box>
                                <Button onClick={() => clearSettings()}>
                                    <FormattedMessage defaultMessage="verwijder configuratie" />
                                </Button>
                            </Flex>
                        </>
                    )}
                </>
            )}
        </FormScreen>
    );
};

export default PartnerConnection;
